import type { DemoScenario } from './demo.js';

/**
 * Аналитика знакомства с игрой (концепция v0.3, п. 4; ТЗ демо, пп. 8 и 11.6;
 * docs/ANALYTICS.md).
 *
 * Журнал событий без личного содержания: что произошло, когда, в какой
 * партии и по какому сценарию. Ответы на карты, личные запросы и игровые
 * переживания сюда не попадают — для маркетинговой аналитики они не нужны.
 * Кто именно совершил событие, видит только администратор.
 */

export const ANALYTICS_EVENT_TYPES = [
  'registered',
  'demo_created',
  'demo_started',
  'demo_start_talent',
  'demo_sector_completed',
  'demo_finished',
  'demo_offer_click',
  'demo_feedback_clear',
  'demo_feedback_full',
  /* Не пишется в журнал: берётся из заказов со статусом «права выданы». */
  'payment_confirmed',
] as const;
export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

export const ANALYTICS_EVENT_LABELS: Record<AnalyticsEventType, string> = {
  registered: 'Регистрация',
  demo_created: 'Создана демопартия',
  demo_started: 'Демопартия началась',
  demo_start_talent: 'Талант от рождения (Маяк пройден)',
  demo_sector_completed: 'Сектор пройден',
  demo_finished: 'Итог демо',
  demo_offer_click: 'Кнопка предложения в итоге',
  demo_feedback_clear: 'Ответ: понятно ли, как играть',
  demo_feedback_full: 'Ответ: хочет ли попробовать полную версию',
  payment_confirmed: 'Подтверждённая оплата',
};

/** Сектора маршрута демо — для подписи события «сектор пройден». */
export const DEMO_SECTOR_TITLES: Readonly<Record<string, string>> = {
  lighthouse: 'Маяк',
  dragon: 'Дракон',
  'castle-key': 'Замок и ключ',
};

/** Партию закрыли, когда все прошли маршрут, или раньше. */
export const DEMO_FINISH_KINDS = ['normal', 'early'] as const;
export type DemoFinishKind = (typeof DEMO_FINISH_KINDS)[number];

export const DEMO_FINISH_LABELS: Record<DemoFinishKind, string> = {
  normal: 'обычный: все прошли три сектора',
  early: 'досрочный: партию завершили раньше',
};

/**
 * Какую кнопку нажали в итоге демо (ТЗ демо, п. 11.6): владелец полного
 * доступа переходит к своей игре — это не интерес к покупке и считается
 * отдельно. Какая из двух, решает сервер по доступу, а не клиент.
 */
export const DEMO_OFFER_KINDS = ['own_game', 'purchase'] as const;
export type DemoOfferKind = (typeof DEMO_OFFER_KINDS)[number];

export const DEMO_OFFER_LABELS: Record<DemoOfferKind, string> = {
  own_game: 'переход к своей полной игре',
  purchase: 'интерес к покупке',
};

/**
 * Два необязательных вопроса после итога (концепция, п. 1.9; ТЗ демо, п. 8).
 * Ответ «хочу попробовать» — не оплата и с ней не смешивается.
 */
export const DEMO_FEEDBACK_QUESTIONS = {
  clear: {
    text: 'Было ли понятно, как играть?',
    answers: { yes: 'Да', partly: 'Не всё', no: 'Нет' },
  },
  full: {
    text: 'Хотели бы вы попробовать полную версию?',
    answers: { yes: 'Да', maybe: 'Пока не знаю', no: 'Нет' },
  },
} as const;
export type DemoFeedbackQuestion = keyof typeof DEMO_FEEDBACK_QUESTIONS;
export type DemoFeedbackAnswer<Q extends DemoFeedbackQuestion = DemoFeedbackQuestion> =
  keyof (typeof DEMO_FEEDBACK_QUESTIONS)[Q]['answers'] & string;

export const DEMO_FEEDBACK_EVENT: Record<DemoFeedbackQuestion, AnalyticsEventType> = {
  clear: 'demo_feedback_clear',
  full: 'demo_feedback_full',
};

/** Ответы владельца партии; `null` — на вопрос не ответили. */
export type DemoFeedbackView = {
  clear: DemoFeedbackAnswer<'clear'> | null;
  full: DemoFeedbackAnswer<'full'> | null;
};

/** Человекочитаемая расшифровка поля «подробности» события. */
export function analyticsDetailLabel(type: AnalyticsEventType, detail: string | null): string {
  if (detail === null) return '';
  switch (type) {
    case 'demo_sector_completed':
      return DEMO_SECTOR_TITLES[detail] ?? detail;
    case 'demo_finished':
      return DEMO_FINISH_LABELS[detail as DemoFinishKind] ?? detail;
    case 'demo_offer_click':
      return DEMO_OFFER_LABELS[detail as DemoOfferKind] ?? detail;
    case 'demo_feedback_clear':
      return (DEMO_FEEDBACK_QUESTIONS.clear.answers as Record<string, string>)[detail] ?? detail;
    case 'demo_feedback_full':
      return (DEMO_FEEDBACK_QUESTIONS.full.answers as Record<string, string>)[detail] ?? detail;
    default:
      return detail;
  }
}

/* -------------------------------------------------------------------------- */
/* Отчёт для администратора                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Воронка по людям, зарегистрированным за период: сколько из них прошли
 * каждый следующий шаг — когда угодно, до сегодняшнего дня.
 */
export type AnalyticsFunnel = {
  registered: number;
  /** Создали хотя бы одну демопартию. */
  demoCreated: number;
  /** Хотя бы одна их демопартия завершилась — обычно или досрочно. */
  demoFinished: number;
  /** Нажали кнопку предложения полной игры в итоге демо (не переход к своей игре). */
  offerClicked: number;
  /** Есть подтверждённая оплата. */
  paid: number;
};

/** Сравнение сценариев по демопартиям, созданным за период. */
export type AnalyticsScenarioStats = {
  scenario: DemoScenario;
  /** Разных владельцев партий. */
  people: number;
  created: number;
  started: number;
  startTalent: number;
  /** Сектора после Маяка, по порядку маршрута: сколько партий их прошли. */
  sectors: { sectorId: string; title: string; count: number }[];
  finishedNormal: number;
  finishedEarly: number;
  ownGameClicks: number;
  purchaseClicks: number;
  /**
   * Охват опроса: сколько партий ответили хотя бы на один вопрос. Из ответов
   * по вопросам его не вывести — на один вопрос могли ответить, на второй нет.
   */
  feedbackParties: number;
  feedback: {
    clear: Record<DemoFeedbackAnswer<'clear'>, number>;
    full: Record<DemoFeedbackAnswer<'full'>, number>;
  };
};

export type AnalyticsSourceRow = {
  /** `utm_source` из ссылки регистрации; `null` — источник не передан. */
  source: string | null;
  registered: number;
  demoCreated: number;
  paid: number;
};

export type AnalyticsSummary = {
  from: string | null;
  to: string | null;
  /** С какого момента ведётся журнал (первое записанное событие). */
  trackingSince: string | null;
  funnel: AnalyticsFunnel;
  scenarios: AnalyticsScenarioStats[];
  sources: AnalyticsSourceRow[];
};

/** Строка списка событий в админке. */
export type AnalyticsEventRow = {
  id: string;
  type: AnalyticsEventType;
  occurredAt: string;
  scenario: DemoScenario | null;
  detail: string | null;
  sessionId: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  /** Служебный номер; `null` — аккаунт удалён и событие обезличено. */
  userId: string | null;
  /** Имя и почта — только в админке, в выгрузку не попадают. */
  userDisplayName: string | null;
  userEmail: string | null;
};

export type AnalyticsEventsPage = {
  items: AnalyticsEventRow[];
  /** Сколько всего событий под фильтром; на экране — последние. */
  total: number;
};
